import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { toastErrors } from '@/shared/config';
import { useAppDispatch } from '@/shared/hooks';
import { debug, trackEvent } from '@/shared/lib';
import { toastCaller } from '@/shared/ui';

import { getTranscriptorErrorMessage } from '../lib';
import { reprocessConsultation } from '../model/transcriptorSlice.thunks';

/**
 * Re-order dispatches reprocessConsultation: the backend consumes a fresh
 * transcriptor order on reprocess, and its fulfilled reducer flips the
 * consultation to "processing" in the store.
 */
export const useReorderConsultation = () => {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();

  return useCallback(async (consultationId: string): Promise<boolean> => {
    trackEvent({
      eventName: 'PatientProfilePageTranscriptorReorderButtonClicked',
    });
    try {
      await dispatch(reprocessConsultation({ consultationId })).unwrap();
      return true;
    } catch (error) {
      debug('Error re-ordering consultation:', error);

      const refusal = getTranscriptorErrorMessage(error);

      toastCaller({
        type: 'error',
        heading: formatMessage(toastErrors.errorHeading),
        message: refusal
          ? formatMessage(refusal)
          : formatMessage({
              id: 'consultationOptions.reorder.error',
              defaultMessage:
                'Failed to re-order the consultation. Please try again.',
            }),
      });
      return false;
    }
  }, []);
};
